'use client';

import { useEffect, useRef } from 'react';

/** Where the sticky column's top sits when it fits the window: under the bar (pt-beat). */
const BAR_CLEARANCE = 112;
/** The gap kept under the column's foot when it is taller than the window. */
const FOOT_GAP = 16;

/**
 * A sticky column that is taller than the window would lose its foot, so its `top` goes
 * negative by the difference: it scrolls with the page until its foot shows, then sticks
 * there (the way X's right column does). Written as `--side-top`; CSS reads it.
 */
export function useStickyTop<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const place = () => {
      const top = Math.min(BAR_CLEARANCE, window.innerHeight - el.offsetHeight - FOOT_GAP);
      el.style.setProperty('--side-top', `${top}px`);
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(el);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, []);
  return ref;
}
